import React from 'react';
import {
  createBrowserRouter,
  Outlet
} from 'react-router-dom';
import EnumValues from './Components/Queries/EnumValues.js';
import App from './App.js';
import Error404 from './Views/Error/Error404.js';
import Error401 from './Views/Error/Error401.js';
import Home from './Views/Home/Home.js';
import Subscriptions from './Views/Subscriptions/Subscriptions.js';
import Customer from './Views/Customer/Customer.js';
import Login from './Views/Customer/Login/Login.js';
import Logout from './Views/Customer/Login/Logout.js';
import Register from './Views/Customer/Register/Register.js';
import Admin from './Views/Admin/Admin.js';
import RequireAuth from './Components/RequireAuth.js';
import ImpersonateUser from './Views/Admin/Users/ImpersonateUser.js';
import ForgottenPassword from './Views/Customer/Login/ForgottenPassword.js';
import ResetPassword from './Views/Customer/Login/ResetPassword.js';
import RegisterPassword from './Views/Customer/Register/RegisterPassword.js';
import SetHandDetails from './Views/Customer/Store/Hand/SetHandDetails.js';
import CollectionHandDetails from './Views/Customer/Store/Hand/CollectionHandDetails.js';
import ArticleHandDetails from './Views/Customer/Store/Hand/ArticleHandDetails.js';
import CheckoutWrapper from './Views/Customer/Store/Checkout/CheckoutWrapper.js';
import IdealPaymentLanding from './Views/Customer/Store/Checkout/IdealPaymentLanding.js';
import StripeWrapper from './Components/Store/Checkout/StripeWrapper.js';
import { getEnvVar } from './helpers/env.js';

import {
  AllProducts,
  Article,
  Author,
  Authors,
  Help,
  Library,
  LinkedProduct,
  Products,
  GiftVouchers,
  VoucherWrapper,
  ProductWrapper,
  SearchResults,
  Subscription
} from './Views/Customer';
import {
  Account,
  Password,
  Personal,
  AccountPurchases,
  Payment,
  Subscriptions as AccountSubscriptions
} from './Views/Customer/Account';
import {
  Authors as AdminAuthors,
  AuthorPurchases,
  Dashboard,
  HandDetails,
  HandJsonDownload,
  Purchases as AdminPurchases,
  Products as AdminProducts,
  ProductDetails,
  PurchasePage,
  AddProduct,
  RoyaltiesOverview,
  Tags,
  Users,
  Vouchers,
  Subscriptions as AdminSubscriptions,
  AddSubscription,
  EditSubscription,
  AddArticle,
  EditArticle
} from './Views/Admin';
import TestGridSkeleton from './__tests__/TestGridSkeleton.js';

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <Error404 />,
    children: [
      {
        index: true,
        element: <Home />
      },
      {
        path: 'skeletons',
        element: <TestGridSkeleton />
      },
      {
        path: 'logout',
        element: <Logout />
      },
      {
        path: 'impersonate',
        element: <ImpersonateUser />
      },
      {
        path: '/:language?/products/:linkId?/:setId/:setTitle/hands/:handId/:handTitle/:embed?',
        element: <SetHandDetails />
      },
      {
        path: '/:language?/sets/:setId/:setTitle/hands/:handId/:handTitle',
        element: <SetHandDetails />
      },
      {
        path: '/:language?/collections/:collectionId/sets/:setId/:setTitle/hands/:handId/:handTitle',
        element: <CollectionHandDetails />
      },
      {
        path: '/:language?/articles/:articleId/:articleTitle/hands/:handId/:handTitle',
        element: <ArticleHandDetails />
      },
      {
        path: '/:language/products/:linkId',
        element: <LinkedProduct />
      },
      {
        path: 'checkout',
        element: (
          <StripeWrapper stripeApiKey={getEnvVar('STRIPE_API_KEY')}>
            <CheckoutWrapper />
          </StripeWrapper>
        )
      },
      {
        path: 'checkout/idealpayment',
        element: (
          <IdealPaymentLanding />
        )
      },
      {
        path: 'subscriptions',
        element: (
          <Subscriptions />
        )
      },
      {
        element: <Customer />,
        children: [
          {
            path: 'search',
            element: <SearchResults />,
            handle: {
              crumb: 'search-results'
            }
          },
          {
            path: 'products',
            element: <Outlet />,
            handle: {
              crumb: 'store'
            },
            children: [
              {
                index: true,
                element: <Products />,
                handle: {
                  showVoucherBanner: true
                }
              },
              {
                path: 'vouchers',
                element: <Outlet />,
                handle: {
                  crumb: 'gift-vouchers'
                },
                children: [
                  {
                    index: true,
                    element: <GiftVouchers />
                  },
                  {
                    path: ':id/:title',
                    element: <VoucherWrapper />,
                    handle: {
                      crumb: 'voucher'
                    }
                  }
                ]
              },
              {
                path: 'all',
                element: <AllProducts />,
                handle: {
                  crumb: 'all-products',
                  showVoucherBanner: true
                }
              },
              {
                path: ':language?/:id/:title',
                element: <ProductWrapper />,
                handle: {
                  crumb: ':title',
                  showVoucherBanner: true
                }
              }
            ]
          },
          {
            path: 'collections',
            element: <Outlet />,
            handle: {
              crumb: 'products',
              showVoucherBanner: true
            },
            children: [
              {
                path: ':id/:title',
                element: <ProductWrapper />,
                handle: {
                  crumb: ':title'
                }
              }
            ]
          },
          {
            path: 'sets',
            element: <Outlet />,
            handle: {
              crumb: 'products',
              showVoucherBanner: true
            },
            children: [
              {
                path: ':id/:title',
                element: <ProductWrapper />,
                handle: {
                  crumb: ':title'
                }
              }
            ]
          },
          {
            path: 'subscriptions',
            element: <Outlet />,
            handle: {
              crumb: 'subscriptions'
            },
            children: [
              {
                path: ':subscriptionId/:subscriptionTitle',
                element: <Outlet />,
                handle: {
                  crumb: ':subscriptionTitle',
                  crumbColour: '#fff'
                },
                children: [
                  {
                    index: true,
                    element: <Subscription />
                  },
                  {
                    path: 'articles',
                    element: <Article />,
                    handle: {
                      crumb: ':subscriptionTitle',
                      crumbColour: '#fff'
                    }
                  },
                  {
                    path: 'articles/:articlePublicId/:articleTitle/:memberful?',
                    element: <Article />,
                    handle: {
                      crumb: ':articleTitle',
                      crumbColour: '#fff'
                    }
                  }
                ]
              }
            ]
          },
          {
            path: 'library',
            element: <Library />,
            handle: {
              crumb: 'my-library',
              showVoucherBanner: true
            }
          },
          {
            path: '/authors',
            element: <Outlet />,
            handle: {
              crumb: 'authors'
            },
            children: [
              {
                index: true,
                element: <Authors />
              },
              {
                path: ':authorId/:authorName',
                element: <Author />,
                handle: {
                  crumb: ':authorName',
                  crumbColour: '#fff'
                }
              }
            ]
          },
          {
            path: 'account',
            element: <Account />,
            handle: {
              crumb: 'account'
            },
            children: [
              {
                index: true,
                element: <Personal />,
                handle: {
                  crumb: 'personal'
                }
              },
              {
                path: 'payment',
                element: (
                  <StripeWrapper stripeApiKey={getEnvVar('STRIPE_API_KEY')}>
                    <Payment />
                  </StripeWrapper>
                ),
                handle: {
                  crumb: 'payment'
                }
              },
              {
                path: 'purchases',
                element: <AccountPurchases />,
                handle: {
                  crumb: 'purchases'
                }
              },
              {
                path: 'password',
                element: <Password />,
                handle: {
                  crumb: 'password'
                }
              },
              {
                path: 'subscriptions',
                element: <AccountSubscriptions />,
                handle: {
                  crumb: 'subscriptions'
                }
              }
            ]
          },
          {
            path: 'login',
            element: <Login />,
            handle: {
              crumb: 'login'
            }
          },
          {
            path: 'register',
            element: <Register />,
            handle: {
              crumb: 'register'
            }
          },
          {
            path: 'forgottenpassword',
            element: <ForgottenPassword />
          },
          {
            path: 'resetpassword/:token',
            element: <ResetPassword />
          },
          {
            path: 'register/:token',
            element: <RegisterPassword />
          },
          {
            path: 'help',
            element: <Help />
          },
          {
            path: 'error401',
            element: <Error401 />
          }
        ]
      },
      {
        element: <Admin />,
        children: [
          {
            path: 'admin',
            element: <Outlet />,
            children: [
              {
                index: true,
                element: (
                  <RequireAuth authorisedUserTypes={['Admin', 'Publisher', 'Editor']}>
                    <Dashboard />
                  </RequireAuth>
                )
              },
              {
                path: 'authors',
                element: (
                  <RequireAuth authorisedUserTypes={['Admin']}>
                    <AdminAuthors />
                  </RequireAuth>
                )
              },
              {
                path: 'subscriptions',
                element: (
                  <RequireAuth authorisedUserTypes={['Admin', 'Publisher', 'Editor']}>
                    <Outlet />
                  </RequireAuth>
                ),
                children: [
                  {
                    index: true,
                    element: <AdminSubscriptions />
                  },
                  {
                    path: ':subscriptionId/:subscriptionTitle',
                    element: <EditSubscription />
                  },
                  {
                    path: 'add',
                    element: <AddSubscription />
                  },
                  {
                    path: ':subscriptionId/:subscriptionTitle/articles/:articleId/:articleTitle',
                    element: <EditArticle />
                  },
                  {
                    path: ':subscriptionId/:subscriptionTitle/articles/add',
                    element: <AddArticle />
                  },
                  {
                    path: ':subscriptionId/:subscriptionTitle/articles/:parentId/:parentTitle/hands/add',
                    element: <HandDetails />
                  },
                  {
                    path: ':subscriptionId/:subscriptionTitle/articles/:parentId/:parentTitle/hands/:handId/:handTitle',
                    element: <HandDetails />
                  },
                  {
                    path: 'set/:parentId/:parentTitle/:parentId/:parentTitle/hands/:handId/:handTitle/download',
                    element: <HandJsonDownload />
                  }
                ]
              },
              {
                path: 'products',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: <AdminProducts />
                  },
                  {
                    path: ':type/add',
                    element: <AddProduct />
                  },
                  {
                    path: ':type/:id/:title',
                    element: <ProductDetails />
                  },
                  {
                    path: 'set/:parentId/:parentTitle/hands/add',
                    element: <HandDetails />
                  },
                  {
                    path: 'set/:parentId/:parentTitle/hands/:handId/:handTitle',
                    element: <HandDetails />
                  },
                  {
                    path: 'set/:parentId/:parentTitle/:parentId/:parentTitle/hands/:handId/:handTitle/download',
                    element: <HandJsonDownload />
                  }
                ]
              },
              {
                path: 'royalties',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: (
                      <RequireAuth authorisedUserTypes={['Admin']}>
                        <EnumValues name='CurrencyEnum'>
                          {currencies => (
                            <RoyaltiesOverview currencies={currencies} />
                          )}
                        </EnumValues>
                      </RequireAuth>
                    )
                  },
                  {
                    path: ':id/:author',
                    element: (
                      <RequireAuth authorisedUserTypes={['Admin']}>
                        <EnumValues name='CurrencyEnum'>
                          {currencies => (
                            <AuthorPurchases currencies={currencies} />
                          )}
                        </EnumValues>
                      </RequireAuth>
                    )
                  }
                ]
              },
              {
                path: 'sales',
                element: <Outlet />,
                children: [
                  {
                    index: true,
                    element: (
                      <RequireAuth authorisedUserTypes={['Admin']}>
                        <AdminPurchases />
                      </RequireAuth>
                    )
                  },
                  {
                    path: ':id',
                    element: (
                      <RequireAuth authorisedUserTypes={['Admin']}>
                        <PurchasePage />
                      </RequireAuth>
                    )
                  }
                ]
              },
              {
                path: 'tags',
                element: (
                  <RequireAuth authorisedUserTypes={['Admin']}>
                    <Tags />
                  </RequireAuth>
                )
              },
              {
                path: 'users',
                element: (
                  <RequireAuth authorisedUserTypes={['Admin']}>
                    <Users />
                  </RequireAuth>
                )
              },
              {
                path: 'vouchers',
                element: (
                  <RequireAuth authorisedUserTypes={['Admin']}>
                    <Vouchers />
                  </RequireAuth>
                )
              }
            ]
          }
        ]
      }
    ]
  }
]);

export default router;
